import React, { useCallback, useEffect, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { getPortalToken } from '../../lib/authToken';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const portalHeaders = () => ({
  Authorization: `Bearer ${getPortalToken()}`,
  'Content-Type': 'application/json',
});

export default function PortalSignPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<any>(null);
  const [disclosure, setDisclosure] = useState('');
  const [consentOnFile, setConsentOnFile] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [checksum, setChecksum] = useState('');
  const [pdfRendered, setPdfRendered] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [typedName, setTypedName] = useState('');
  const [password, setPassword] = useState('');
  const [consentToSign, setConsentToSign] = useState(false);
  const [acceptRecords, setAcceptRecords] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    const token = getPortalToken();
    if (!token) {
      navigate(`/portal/login?next=/portal/sign/${requestId}`);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`/api/portal/signature-requests/${requestId}`, {
        headers: portalHeaders(),
      });
      if (res.status === 401) {
        navigate(`/portal/login?next=/portal/sign/${requestId}`);
        return;
      }
      const body = await res.json();
      if (!res.ok) throw new Error(body.message || 'Unable to load signature request');
      setMeta(body.data);
      setDisclosure(body.disclosure || '');
      setConsentOnFile(Boolean(body.consumerConsentOnFile));
      const docRes = await fetch(`/api/portal/signature-requests/${requestId}/document`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!docRes.ok) throw new Error('Unable to load the document PDF');
      const hash = docRes.headers.get('X-Document-Checksum') || body.data?.sourceChecksum;
      setChecksum(hash || '');
      const blob = await docRes.blob();
      setPdfUrl(URL.createObjectURL(blob));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to open signing page');
    } finally {
      setLoading(false);
    }
  }, [navigate, requestId]);

  useEffect(() => {
    load();
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const saveConsent = async () => {
    const res = await fetch('/api/portal/electronic-records-consent', {
      method: 'POST',
      headers: portalHeaders(),
    });
    const body = await res.json();
    if (!res.ok) throw new Error(body.message || 'Could not record consent');
    setConsentOnFile(true);
  };

  const sign = async () => {
    if (!pdfRendered) {
      toast.error('Wait until the PDF has finished displaying.');
      return;
    }
    if (!consentOnFile) {
      if (!acceptRecords) {
        toast.error('Accept electronic records consent first.');
        return;
      }
    }
    if (!consentToSign) {
      toast.error('Confirm you intend to sign this document.');
      return;
    }
    setSubmitting(true);
    try {
      if (!consentOnFile) await saveConsent();
      const res = await fetch(`/api/portal/signature-requests/${requestId}/sign`, {
        method: 'POST',
        headers: portalHeaders(),
        body: JSON.stringify({
          typedName: typedName.trim(),
          password,
          pdfRendered: true,
          presentedChecksum: checksum,
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.message || 'Signature failed');
      toast.success('Signed. A copy is in your portal documents.');
      navigate('/portal');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Signature failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Typography sx={{ p: 4 }}>Loading signing page…</Typography>;
  }

  if (meta?.status === 'completed') {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="success">This document is already signed.</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/portal')}>Back to portal</Button>
      </Container>
    );
  }

  return (
    <Box>
      <AppBar position="static">
        <Toolbar>
          <Typography sx={{ flex: 1 }}>{meta?.title || 'Sign document'}</Typography>
          <Button color="inherit" onClick={() => navigate('/portal')}>Portal</Button>
        </Toolbar>
      </AppBar>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          You are viewing the exact PDF that will be bound to your signature (SHA-256 frozen at send).
          This is not a notarization and is not for wills or court originals.
        </Alert>
        {pdfUrl && (
          <Box sx={{ border: '1px solid', borderColor: 'divider', mb: 2, overflow: 'auto', maxHeight: 480 }}>
            <Document
              file={pdfUrl}
              onLoadSuccess={({ numPages: n }) => {
                setNumPages(n);
                setPdfRendered(true);
              }}
              onLoadError={() => toast.error('PDF failed to display')}
            >
              {Array.from({ length: numPages }, (_, i) => (
                <Page key={i + 1} pageNumber={i + 1} width={720} />
              ))}
            </Document>
          </Box>
        )}
        {!consentOnFile && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>{disclosure}</Typography>
            <FormControlLabel
              control={<Checkbox checked={acceptRecords} onChange={(e) => setAcceptRecords(e.target.checked)} />}
              label="I agree to receive and sign records electronically"
            />
          </Box>
        )}
        <FormControlLabel
          control={<Checkbox checked={consentToSign} onChange={(e) => setConsentToSign(e.target.checked)} />}
          label={`I intend to sign "${meta?.title || 'this document'}"`}
        />
        <TextField
          fullWidth
          label="Full legal name"
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
          sx={{ mt: 2 }}
        />
        <TextField
          fullWidth
          type="password"
          label="Re-enter portal password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          sx={{ mt: 2 }}
        />
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          disabled={submitting || !pdfRendered || typedName.trim().length < 2 || !password}
          onClick={sign}
        >
          {submitting ? 'Signing…' : 'Sign'}
        </Button>
      </Container>
    </Box>
  );
}
